import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, ArrowUp, FolderOpen, FileText, Download } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = 'https://baustellenmgmt.mas123.de/api';

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatPercent(value) {
  const numeric = Number(value || 0);
  if (Number.isNaN(numeric)) return '0 %';
  return `${(numeric * 100).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 1 })} %`;
}

function getContrastTextColor(hex) {
  if (!hex || typeof hex !== 'string') return '#111827';
  const safe = hex.startsWith('#') ? hex.slice(1) : hex;
  if (safe.length !== 6) return '#111827';
  const num = parseInt(safe, 16);
  if (Number.isNaN(num)) return '#111827';
  const r = num >> 16;
  const g = (num >> 8) & 0xff;
  const b = num & 0xff;
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance > 160 ? '#111827' : '#ffffff';
}

function getExtractedItems(data) {
  if (!data || typeof data !== 'object') return [];
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.line_items)) return data.line_items;
  if (Array.isArray(data.lines)) return data.lines;
  return [];
}

function sanitizeFilenamePart(value) {
  return String(value || '')
    .replace(/[\\/:*?"<>|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function toDateToken(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const mDe = raw.match(/^(\d{2})\.(\d{2})\.(\d{2,4})$/);
  if (mDe) {
    const day = mDe[1];
    const month = mDe[2];
    const year = mDe[3].length === 2 ? `20${mDe[3]}` : mDe[3];
    return `${day}.${month}.${year}`;
  }
  const mIso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (mIso) {
    return `${mIso[3]}.${mIso[2]}.${mIso[1]}`;
  }
  return raw;
}

function splitNameAndExt(filename) {
  const name = String(filename || '').trim();
  const idx = name.lastIndexOf('.');
  if (idx <= 0 || idx === name.length - 1) return { base: name || 'rechnung', ext: 'pdf' };
  return { base: name.slice(0, idx), ext: name.slice(idx + 1) };
}

function compareByLabelDe(a, b) {
  return String(a || '').localeCompare(String(b || ''), 'de', { sensitivity: 'base' });
}

function normalizeConfidence(value) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return null;
  if (parsed > 1) return Math.max(0, Math.min(1, parsed / 100));
  return Math.max(0, Math.min(1, parsed));
}

function getConfidenceMeta(value) {
  const confidence = normalizeConfidence(value);
  if (confidence === null) {
    return { label: '-', badge: 'unbekannt', color: '#6b7280', background: '#f3f4f6' };
  }
  if (confidence >= 0.8) {
    return {
      label: `${Math.round(confidence * 100)} %`,
      badge: 'hoch',
      color: '#065f46',
      background: '#d1fae5',
    };
  }
  if (confidence >= 0.5) {
    return {
      label: `${Math.round(confidence * 100)} %`,
      badge: 'mittel',
      color: '#92400e',
      background: '#fef3c7',
    };
  }
  return {
    label: `${Math.round(confidence * 100)} %`,
    badge: 'niedrig',
    color: '#991b1b',
    background: '#fee2e2',
  };
}

function getSuggestedTypeId(row, availableTypes = []) {
  const directId = row?.positions_typ || row?.positions_typ_id || row?.type_suggestion?.id;
  if (directId) return String(directId);
  const directKey = row?.positions_typ_key_snapshot || row?.type_suggestion?.key;
  if (!directKey) return '';
  const matched = availableTypes.find((entry) => String(entry?.key || '') === String(directKey));
  return matched ? String(matched.id) : '';
}

function getMatchMeta(value) {
  const score = normalizeConfidence(value);
  if (score === null) {
    return { label: '-', badge: 'unbekannt', color: '#6b7280', background: '#f3f4f6' };
  }
  if (score >= 0.8) {
    return {
      label: `${Math.round(score * 100)} %`,
      badge: 'hoch',
      color: '#065f46',
      background: '#d1fae5',
    };
  }
  if (score >= 0.5) {
    return {
      label: `${Math.round(score * 100)} %`,
      badge: 'mittel',
      color: '#92400e',
      background: '#fef3c7',
    };
  }
  return {
    label: `${Math.round(score * 100)} %`,
    badge: 'niedrig',
    color: '#991b1b',
    background: '#fee2e2',
  };
}

function formatMatchSource(value) {
  if (value === 'artikelstamm') return 'Artikelstamm';
  if (value === 'historie') return 'Historie';
  if (value === 'manual') return 'Manuell';
  return value || '-';
}

function formatSupplierAlarmType(value) {
  if (value === 'low_type_confidence_rate') return 'Typ schwach';
  if (value === 'low_match_rate') return 'Match schwach';
  if (value === 'high_uncertain_rate') return 'Viele unsichere';
  if (value === 'high_manual_review_rate') return 'Viel Nacharbeit';
  if (value === 'template_quality_issue') return 'Template-Thema';
  return value || 'Alarm';
}

function getMatchCandidates(row) {
  if (Array.isArray(row?.match_suggestions) && row.match_suggestions.length) {
    return row.match_suggestions;
  }
  if (Array.isArray(row?.match_payload?.candidates) && row.match_payload.candidates.length) {
    return row.match_payload.candidates;
  }
  const bestMatch = row?.match_payload?.best_match;
  return bestMatch ? [bestMatch] : [];
}

function getPrimaryMatch(row) {
  if (row?.selected_match_candidate && typeof row.selected_match_candidate === 'object') {
    return row.selected_match_candidate;
  }
  if (row?.match_payload?.best_match && typeof row.match_payload.best_match === 'object') {
    return row.match_payload.best_match;
  }
  const candidates = getMatchCandidates(row);
  return candidates[0] || null;
}

function getMatchCandidateLabel(candidate) {
  if (!candidate || typeof candidate !== 'object') return 'Unbekannter Vorschlag';
  return candidate.name
    || candidate.bezeichnung
    || candidate.artikelnummer
    || candidate.kostenposition_id
    || 'Vorschlag';
}

function getResolvedTypeKey(row, availableTypes = []) {
  const directKey = row?.positions_typ_key_snapshot || row?.type_suggestion?.key;
  if (directKey) return String(directKey);
  const directId = row?.positions_typ || row?.positions_typ_id || row?.type_suggestion?.id;
  if (!directId) return '';
  const matched = availableTypes.find((entry) => String(entry?.id) === String(directId));
  return matched?.key || '';
}

function getReviewMeta(status) {
  if (status === 'kritisch') {
    return { label: 'kritisch', color: '#991b1b', background: '#fee2e2', border: '#ef4444' };
  }
  if (status === 'pruefen') {
    return { label: 'prüfen', color: '#92400e', background: '#fef3c7', border: '#f59e0b' };
  }
  return { label: 'ok', color: '#065f46', background: '#d1fae5', border: '#10b981' };
}

function evaluateReviewStatus(row, availableTypes = []) {
  const reasons = [];
  const criticalReasons = [];
  const typeConfidence = normalizeConfidence(row?.type_confidence ?? row?.typ_confidence);
  const matchScore = normalizeConfidence(row?.match_score);
  const typeKey = getResolvedTypeKey(row, availableTypes);
  const unit = String(row?.unit || '').trim().toLowerCase();
  const description = String(row?.description || '').trim();
  const primaryMatch = getPrimaryMatch(row);
  const matchSource = String(row?.match_source || primaryMatch?.source || '').trim();

  if (!typeKey) {
    criticalReasons.push('Kein Typ gesetzt');
  } else if (typeConfidence !== null && typeConfidence < 0.5) {
    reasons.push(`Typ unsicher (${Math.round(typeConfidence * 100)} %)`);
  }

  if (typeKey && !row?.typ_manually_confirmed) {
    reasons.push('Typ noch nicht manuell bestätigt');
  }

  if (!matchSource || row?.match_status === 'none' || row?.match_status === 'rejected' || !primaryMatch) {
    criticalReasons.push('Kein belastbarer Match-Vorschlag vorhanden');
  } else if (matchScore !== null && matchScore < 0.5) {
    reasons.push(`Match schwach (${Math.round(matchScore * 100)} %)`);
  }

  if (primaryMatch && !row?.match_manually_confirmed && row?.match_status !== 'confirmed') {
    reasons.push('Match noch nicht manuell bestätigt');
  }

  if (!description || description.length < 4) {
    criticalReasons.push('Beschreibung sehr kurz oder leer');
  }
  if (row?.quantity === null || row?.quantity === undefined || row?.quantity === '') {
    reasons.push('Menge fehlt');
  }
  if (!unit) {
    reasons.push('Einheit fehlt');
  }
  if (row?.unit_price === null || row?.unit_price === undefined || row?.unit_price === '') {
    reasons.push('Einzelpreis fehlt');
  }

  if (typeKey === 'material' && ['h', 'std', 'stunden'].includes(unit)) {
    criticalReasons.push('Material-Typ, aber Stunden-Einheit');
  }
  if (typeKey === 'arbeitsstunden' && unit && !['h', 'std', 'stunden'].includes(unit)) {
    reasons.push('Arbeitsstunden-Typ ohne Stunden-Einheit');
  }
  if (typeKey === 'geraetestunden' && unit && !['h', 'std', 'stunden'].includes(unit)) {
    reasons.push('Gerätestunden-Typ ohne Stunden-Einheit');
  }
  if (typeKey === 'arbeitsstunden' && primaryMatch?.source === 'artikelstamm') {
    reasons.push('Arbeitsstunden-Typ, aber materialartiger Match');
  }

  const allReasons = [...criticalReasons, ...reasons];
  const reviewConfirmed = !!row?.review_confirmed;
  let status = 'ok';
  if (criticalReasons.length) {
    status = 'kritisch';
  } else if (allReasons.length && !reviewConfirmed) {
    status = 'pruefen';
  } else if (!reviewConfirmed && (typeKey || primaryMatch)) {
    status = 'pruefen';
    reasons.push('Noch nicht als geprüft markiert');
  }

  return {
    status,
    reasons: [...criticalReasons, ...reasons].filter(Boolean),
    flags: {
      withoutType: !typeKey,
      withoutMatch: !primaryMatch || !matchSource || row?.match_status === 'none' || row?.match_status === 'rejected',
      lowConfidence: (typeConfidence !== null && typeConfidence < 0.5) || (matchScore !== null && matchScore < 0.5),
      manualChanged: !!row?.typ_manually_confirmed || !!row?.match_manually_confirmed || !!row?.review_confirmed,
      reviewConfirmed,
    },
  };
}

function RechnungenView({ token }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPath, setCurrentPath] = useState('');
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceOcrLoading, setInvoiceOcrLoading] = useState(false);
  const [invoiceError, setInvoiceError] = useState('');
  const [invoiceData, setInvoiceData] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [debugLoading, setDebugLoading] = useState(false);
  const [debugError, setDebugError] = useState('');
  const [debugText, setDebugText] = useState('');
  const [analyseSettings, setAnalyseSettings] = useState({ analyzer: 'invoice2data' });
  const [selectedAnalyzer, setSelectedAnalyzer] = useState('invoice2data');
  const [baustellen, setBaustellen] = useState([]);
  const [supplierSuggestions, setSupplierSuggestions] = useState([]);
  const [rechnungspositionsTypen, setRechnungspositionsTypen] = useState([]);
  const [rechnungspositionsTypenError, setRechnungspositionsTypenError] = useState('');
  const [selectedBaustelle, setSelectedBaustelle] = useState('');
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignSelectedLoading, setAssignSelectedLoading] = useState(false);
  const [splitMode, setSplitMode] = useState(false);
  const [markedIndices, setMarkedIndices] = useState([]);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [newItemForm, setNewItemForm] = useState({
    article_no: '',
    description: '',
    quantity: '',
    unit: '',
    unit_price: '',
    line_total: '',
  });
  const [hoveredPath, setHoveredPath] = useState('');
  const [targetFilename, setTargetFilename] = useState('');
  const [targetFilenameTouched, setTargetFilenameTouched] = useState(false);
  const [filenameSuggestionApplied, setFilenameSuggestionApplied] = useState(false);
  const [reportFilters, setReportFilters] = useState({
    date_from: '',
    date_to: '',
    baustelle: '',
    issuer: '',
  });
  const [reportOverview, setReportOverview] = useState(null);
  const [supplierReport, setSupplierReport] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState('');
  const [reviewFilter, setReviewFilter] = useState('all');
  const [reviewJumpIndex, setReviewJumpIndex] = useState(-1);

  const fileItems = useMemo(() => items.filter((item) => !item.is_dir), [items]);
  const extractedItems = useMemo(() => getExtractedItems(invoiceData), [invoiceData]);
  const extractedItemsTotal = useMemo(() => (
    extractedItems.reduce((sum, row) => {
      const value = row?.line_total;
      if (value === null || value === undefined || value === '') return sum;
      const parsed = Number(String(value).replace(',', '.'));
      return Number.isNaN(parsed) ? sum : sum + parsed;
    }, 0)
  ), [extractedItems]);
  const selectedBaustelleObj = useMemo(
    () => baustellen.find((b) => String(b.id) === String(selectedBaustelle)),
    [baustellen, selectedBaustelle],
  );
  const sortedBaustellen = useMemo(() => (
    [...baustellen].sort((a, b) => (
      compareByLabelDe(a.kunde_name || a.name, b.kunde_name || b.name) || compareByLabelDe(a.nummer, b.nummer)
    ))
  ), [baustellen]);
  const activeRechnungspositionsTypen = useMemo(() => (
    [...rechnungspositionsTypen]
      .filter((entry) => entry?.aktiv !== false)
      .sort((a, b) => (a.sortierung ?? 0) - (b.sortierung ?? 0) || compareByLabelDe(a.label, b.label))
  ), [rechnungspositionsTypen]);
  const reviewedItems = useMemo(() => (
    extractedItems.map((row, index) => ({
      row,
      index,
      review: evaluateReviewStatus(row, activeRechnungspositionsTypen),
    }))
  ), [extractedItems, activeRechnungspositionsTypen]);
  const reviewSummary = useMemo(() => {
    const summary = {
      total: reviewedItems.length,
      open: 0,
      lowConfidence: 0,
      withoutMatch: 0,
      manualChanged: 0,
      confirmed: 0,
    };
    reviewedItems.forEach(({ review }) => {
      if (review.status !== 'ok') summary.open += 1;
      if (review.flags.lowConfidence) summary.lowConfidence += 1;
      if (review.flags.withoutMatch) summary.withoutMatch += 1;
      if (review.flags.manualChanged) summary.manualChanged += 1;
      if (review.flags.reviewConfirmed) summary.confirmed += 1;
    });
    return summary;
  }, [reviewedItems]);
  const filteredReviewedItems = useMemo(() => {
    if (reviewFilter === 'all') return reviewedItems;
    if (reviewFilter === 'review') return reviewedItems.filter((entry) => entry.review.status !== 'ok');
    if (reviewFilter === 'withoutType') return reviewedItems.filter((entry) => entry.review.flags.withoutType);
    if (reviewFilter === 'withoutMatch') return reviewedItems.filter((entry) => entry.review.flags.withoutMatch);
    if (reviewFilter === 'lowConfidence') return reviewedItems.filter((entry) => entry.review.flags.lowConfidence);
    if (reviewFilter === 'manual') return reviewedItems.filter((entry) => entry.review.flags.manualChanged);
    return reviewedItems;
  }, [reviewedItems, reviewFilter]);
  const filenameSuggestion = useMemo(() => {
    const original = selectedItem?.name || 'rechnung.pdf';
    const { base: originalBase, ext } = splitNameAndExt(original);
    const pattern = (analyseSettings?.invoice_filename_pattern || '{{date}}_{{issuer}}_{{invoice_number}}_{{amount}}').trim();
    const replacements = {
      baustelle: sanitizeFilenamePart(selectedBaustelleObj?.name || ''),
      date: sanitizeFilenamePart(toDateToken(invoiceData?.date || '')),
      issuer: sanitizeFilenamePart(invoiceData?.issuer || ''),
      invoice_number: sanitizeFilenamePart(invoiceData?.invoice_number || ''),
      amount: sanitizeFilenamePart(invoiceData?.amount ?? ''),
      currency: sanitizeFilenamePart(invoiceData?.currency || ''),
      original_name: sanitizeFilenamePart(originalBase || ''),
    };
    const rendered = pattern.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (match, key) => {
      const value = replacements[key];
      return value || '';
    });
    const safeBase = sanitizeFilenamePart(rendered || originalBase || 'rechnung').replace(/\s+/g, '_');
    return `${safeBase || 'rechnung'}.${ext || 'pdf'}`;
  }, [selectedItem, analyseSettings, invoiceData, selectedBaustelleObj]);

  useEffect(() => {
    if (!selectedItem) return;
    if (!targetFilenameTouched || !targetFilename) {
      setTargetFilename(filenameSuggestion);
      setFilenameSuggestionApplied(false);
    }
  }, [filenameSuggestion, selectedItem, targetFilenameTouched, targetFilename]);

  useEffect(() => {
    if (!filenameSuggestionApplied) return;
    if ((targetFilename || '') !== (filenameSuggestion || '')) {
      setFilenameSuggestionApplied(false);
    }
  }, [filenameSuggestionApplied, targetFilename, filenameSuggestion]);

  const loadBaustellen = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/baustellen/baustellen/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status: 'AKTIV', page_size: 500 },
      });
      const list = response.data?.results || response.data || [];
      setBaustellen(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Fehler beim Laden der Baustellen:', err);
    }
  }, [token]);

  const loadSupplierSuggestions = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/core/rechnungen/suppliers/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = response.data?.suppliers || [];
      setSupplierSuggestions(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Fehler beim Laden der Lieferanten-Vorschläge:', err);
      setSupplierSuggestions([]);
    }
  }, [token]);

  const loadRechnungspositionsTypen = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/core/rechnungspositions-typen/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = response.data?.results || response.data || [];
      setRechnungspositionsTypen(Array.isArray(list) ? list : []);
      setRechnungspositionsTypenError('');
    } catch (err) {
      console.error('Fehler beim Laden der Rechnungspositions-Typen:', err);
      setRechnungspositionsTypen([]);
      setRechnungspositionsTypenError(err.response?.data?.detail || 'Rechnungspositions-Typen konnten nicht geladen werden.');
    }
  }, [token]);

  const loadReports = useCallback(async () => {
    setReportLoading(true);
    setReportError('');
    try {
      const [overviewResponse, suppliersResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/baustellen/rechnungen/report-overview/`, {
          headers: { Authorization: `Bearer ${token}` },
          params: reportFilters,
        }),
        axios.get(`${API_BASE_URL}/baustellen/rechnungen/report-suppliers/`, {
          headers: { Authorization: `Bearer ${token}` },
          params: reportFilters,
        }),
      ]);
      setReportOverview(overviewResponse.data || null);
      setSupplierReport(suppliersResponse.data?.results || []);
    } catch (err) {
      console.error('Fehler beim Laden der Rechnungs-Auswertungen:', err);
      setReportError(err.response?.data?.detail || err.response?.data?.error || 'Fehler beim Laden der Auswertungen.');
      setReportOverview(null);
      setSupplierReport([]);
    }
    setReportLoading(false);
  }, [token, reportFilters]);

  const loadItems = useCallback(async (path = '') => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/core/rechnungen/files/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { path },
      });
      setCurrentPath(response.data.current_path || '');
      setItems(response.data.items || []);
    } catch (err) {
      console.error('Fehler beim Laden der Rechnungen:', err);
      setError(err.response?.data?.error || 'Fehler beim Laden der Rechnungen');
      setItems([]);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    loadItems('');
  }, [loadItems]);

  useEffect(() => {
    loadBaustellen();
  }, [loadBaustellen]);

  useEffect(() => {
    loadSupplierSuggestions();
  }, [loadSupplierSuggestions]);

  useEffect(() => {
    loadRechnungspositionsTypen();
  }, [loadRechnungspositionsTypen]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  useEffect(() => {
    async function loadAnalyseSettings() {
      try {
        const response = await axios.get(`${API_BASE_URL}/core/rechnungsanalyse-settings/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAnalyseSettings(response.data || { analyzer: 'invoice2data' });
        setSelectedAnalyzer((response.data?.analyzer || 'invoice2data'));
      } catch (err) {
        console.error('Fehler beim Laden der Analyse-Einstellungen:', err);
      }
    }
    loadAnalyseSettings();
  }, [token]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  async function openFile(item) {
    if (!item || item.is_dir) return;
    setSelectedItem(item);
    setInvoiceData({
      issuer: '',
      invoice_number: '',
      date: '',
      amount: '',
      currency: 'EUR',
      items: [],
    });
    setInvoiceError('');
    setDebugText('');
    setDebugError('');
    setMarkedIndices([]);
    setSplitMode(false);
    setTargetFilename(item.name || 'rechnung.pdf');
    setTargetFilenameTouched(false);
    setFilenameSuggestionApplied(false);
    setPreviewLoading(true);
    setPreviewError('');
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl('');
    }
    try {
      const response = await axios.get(`${API_BASE_URL}/core/rechnungen/file-content/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { path: item.path },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: response.headers['content-type'] || item.content_type || 'application/octet-stream' });
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (err) {
      console.error('Fehler beim Laden der Vorschau:', err);
      setPreviewError(err.response?.data?.error || 'Vorschau konnte nicht geladen werden.');
    }
    setPreviewLoading(false);
  }

  async function extractWithInvoice2Data() {
    if (!selectedItem || selectedItem.is_dir) return;
    setInvoiceLoading(true);
    setInvoiceError('');
    try {
      const response = await axios.post(
        `${API_BASE_URL}/core/rechnungen/extract/`,
        { path: selectedItem.path, analyzer: selectedAnalyzer || analyseSettings?.analyzer || 'invoice2data' },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setInvoiceData(response.data?.data || null);
      setMarkedIndices([]);
    } catch (err) {
      console.error('Fehler bei invoice2data:', err);
      setInvoiceError(err.response?.data?.error || 'Fehler beim Auslesen mit invoice2data.');
    }
    setInvoiceLoading(false);
  }

  async function extractWithOcr() {
    if (!selectedItem || selectedItem.is_dir) return;
    setInvoiceOcrLoading(true);
    setInvoiceError('');
    try {
      const response = await axios.post(
        `${API_BASE_URL}/core/rechnungen/extract/`,
        {
          path: selectedItem.path,
          analyzer: selectedAnalyzer || analyseSettings?.analyzer || 'invoice2data',
          force_ocr: true,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setInvoiceData(response.data?.data || null);
      setMarkedIndices([]);
    } catch (err) {
      console.error('Fehler bei OCR-Analyse:', err);
      const backendError = err.response?.data?.error;
      const status = err.response?.status;
      setInvoiceError(backendError || (status ? `Fehler bei der OCR-Analyse (HTTP ${status}).` : 'Fehler bei der OCR-Analyse.'));
    }
    setInvoiceOcrLoading(false);
  }

  async function generateTemplateWithLlm() {
    if (!selectedItem || selectedItem.is_dir) return;
    const suggested = (selectedItem.name || 'template').replace(/\.[^.]+$/, '');
    const enteredName = window.prompt('Template-Name (optional):', suggested);
    if (enteredName === null) return;

    setTemplateLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/core/rechnungen/generate-template/`,
        { path: selectedItem.path, template_name: enteredName || '' },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const templateName = response.data?.template?.name || 'Neues Template';
      alert(`Template erstellt: ${templateName}`);
    } catch (err) {
      console.error('Fehler bei Template-Erstellung:', err);
      alert(err.response?.data?.error || 'Fehler bei der Template-Erstellung');
    }
    setTemplateLoading(false);
  }

  async function loadDebugText() {
    if (!selectedItem || selectedItem.is_dir) return;
    setDebugLoading(true);
    setDebugError('');
    try {
      const response = await axios.post(
        `${API_BASE_URL}/core/rechnungen/debug-text/`,
        { path: selectedItem.path },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setDebugText(response.data?.text_preview || '');
    } catch (err) {
      console.error('Fehler beim Laden des Debug-Texts:', err);
      setDebugError(err.response?.data?.error || 'Fehler beim Laden des Debug-Texts');
      setDebugText('');
    }
    setDebugLoading(false);
  }

  async function loadDebugTextWithOcr() {
    if (!selectedItem || selectedItem.is_dir) return;
    setDebugLoading(true);
    setDebugError('');
    try {
      const response = await axios.post(
        `${API_BASE_URL}/core/rechnungen/debug-text/`,
        { path: selectedItem.path, force_ocr: true },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setDebugText(response.data?.text_preview || '');
    } catch (err) {
      console.error('Fehler beim Laden des OCR-Debug-Texts:', err);
      setDebugError(err.response?.data?.error || 'Fehler beim Laden des OCR-Debug-Texts');
      setDebugText('');
    }
    setDebugLoading(false);
  }

  function goUp() {
    if (!currentPath) return;
    const parts = currentPath.split('/').filter(Boolean);
    parts.pop();
    loadItems(parts.join('/'));
  }

  function downloadSelected() {
    if (!selectedItem || !previewUrl) return;
    const a = document.createElement('a');
    a.href = previewUrl;
    a.download = selectedItem.name || 'rechnung';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  function toggleItemMark(index) {
    if (!splitMode) return;
    setMarkedIndices((prev) => (prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]));
  }

  function openAddItemModal() {
    setNewItemForm({
      article_no: '',
      description: '',
      quantity: '',
      unit: '',
      unit_price: '',
      line_total: '',
    });
    setShowAddItemModal(true);
  }

  function closeAddItemModal() {
    setShowAddItemModal(false);
  }

  function toNumberOrNull(value) {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(String(value).replace(',', '.'));
    return Number.isNaN(parsed) ? null : parsed;
  }

  function saveManualItem() {
    if (!invoiceData || typeof invoiceData !== 'object') {
      alert('Bitte zuerst eine Analyse durchführen.');
      return;
    }
    if (!(newItemForm.description || '').trim()) {
      alert('Bitte mindestens eine Beschreibung eingeben.');
      return;
    }

    const newItem = {
      article_no: (newItemForm.article_no || '').trim(),
      description: (newItemForm.description || '').trim(),
      quantity: toNumberOrNull(newItemForm.quantity),
      unit: (newItemForm.unit || '').trim(),
      unit_price: toNumberOrNull(newItemForm.unit_price),
      line_total: toNumberOrNull(newItemForm.line_total),
      source: 'manual',
    };

    setInvoiceData((prev) => ({
      ...(prev || {}),
      items: [...getExtractedItems(prev), newItem],
      items_source: (prev?.items_source && prev.items_source !== 'manual') ? `${prev.items_source}+manual` : 'manual',
    }));
    closeAddItemModal();
  }

  function updateInvoiceHeaderField(field, value) {
    setInvoiceData((prev) => {
      if (!prev || typeof prev !== 'object') return prev;
      return { ...prev, [field]: value };
    });
  }

  function updateExtractedItem(index, field, value) {
    setInvoiceData((prev) => {
      if (!prev || typeof prev !== 'object') return prev;
      const currentItems = getExtractedItems(prev);
      if (!Array.isArray(currentItems) || index < 0 || index >= currentItems.length) return prev;
      const nextItems = currentItems.map((row, idx) => {
        if (idx !== index) return row;
        return { ...row, [field]: value };
      });
      return { ...prev, items: nextItems };
    });
  }

  function updateExtractedItemPatch(index, patch) {
    setInvoiceData((prev) => {
      if (!prev || typeof prev !== 'object') return prev;
      const currentItems = getExtractedItems(prev);
      if (!Array.isArray(currentItems) || index < 0 || index >= currentItems.length) return prev;
      const nextItems = currentItems.map((row, idx) => {
        if (idx !== index) return row;
        return { ...row, ...patch };
      });
      return { ...prev, items: nextItems };
    });
  }

  function handleTypeSelection(index, typeId) {
    const selectedType = activeRechnungspositionsTypen.find((entry) => String(entry.id) === String(typeId));
    if (!typeId || !selectedType) {
      updateExtractedItemPatch(index, {
        positions_typ: null,
        positions_typ_id: null,
        positions_typ_key_snapshot: '',
        type_suggestion: null,
        type_confidence: null,
        typ_source: '',
        type_source: '',
        typ_manually_confirmed: false,
      });
      return;
    }
    updateExtractedItemPatch(index, {
      positions_typ: selectedType.id,
      positions_typ_id: selectedType.id,
      positions_typ_key_snapshot: selectedType.key || '',
      type_suggestion: { id: selectedType.id, key: selectedType.key, label: selectedType.label },
      type_confidence: 1,
      typ_source: 'manual',
      type_source: 'manual',
      typ_manually_confirmed: true,
    });
  }

  function applyMatchCandidate(index, candidate) {
    if (!candidate || typeof candidate !== 'object') return;
    updateExtractedItemPatch(index, {
      match_status: 'confirmed',
      match_score: candidate.score ?? candidate.match_score ?? 1,
      match_source: candidate.source || '',
      match_payload: {
        ...(candidate.source || candidate.score || candidate.reasoning ? {
          best_match: candidate,
          candidates: getMatchCandidates(getExtractedItems(invoiceData)[index] || {}),
        } : {}),
      },
      selected_match_candidate: candidate,
      match_manually_confirmed: true,
    });
  }

  function ignoreMatch(index) {
    updateExtractedItemPatch(index, {
      match_status: 'rejected',
      match_score: null,
      match_source: 'manual',
      match_payload: {},
      selected_match_candidate: null,
      match_manually_confirmed: false,
    });
  }

  function confirmReview(index) {
    const currentRow = extractedItems[index] || {};
    updateExtractedItemPatch(index, {
      review_confirmed: true,
      review_status: 'ok',
      typ_manually_confirmed: currentRow.typ_manually_confirmed || !!getResolvedTypeKey(currentRow, activeRechnungspositionsTypen),
      match_manually_confirmed: currentRow.match_manually_confirmed || !!getPrimaryMatch(currentRow),
    });
  }

  function resetReview(index) {
    updateExtractedItemPatch(index, {
      review_confirmed: false,
      review_status: '',
    });
  }

  function jumpToNextReviewItem() {
    const openItems = reviewedItems.filter((entry) => entry.review.status !== 'ok');
    if (!openItems.length) return;
    const currentPos = openItems.findIndex((entry) => entry.index === reviewJumpIndex);
    const nextEntry = openItems[(currentPos + 1) % openItems.length];
    setReviewJumpIndex(nextEntry.index);
    const node = document.getElementById(`invoice-row-${nextEntry.index}`);
    if (node) {
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  function removeExtractedItem(index) {
    setInvoiceData((prev) => {
      if (!prev || typeof prev !== 'object') return prev;
      const currentItems = getExtractedItems(prev);
      if (!Array.isArray(currentItems) || index < 0 || index >= currentItems.length) return prev;
      const nextItems = currentItems.filter((_, idx) => idx !== index);
      return { ...prev, items: nextItems };
    });
    setMarkedIndices((prev) => prev.filter((i) => i !== index).map((i) => (i > index ? i - 1 : i)));
  }

  async function assignInvoiceToBaustelle() {
    if (!selectedItem || selectedItem.is_dir) return;
    if (!selectedBaustelle) {
      alert('Bitte zuerst eine aktive Baustelle auswählen.');
      return;
    }
    if (!invoiceData || typeof invoiceData !== 'object') {
      alert('Bitte zuerst eine Analyse durchführen.');
      return;
    }
    setAssignLoading(true);
    setInvoiceError('');
    try {
      await axios.post(
        `${API_BASE_URL}/baustellen/baustellen/rechnungen-zuordnen/`,
        {
          baustelle: selectedBaustelle,
          path: selectedItem.path,
          target_filename: (targetFilename || filenameSuggestion || '').trim(),
          invoice_data: invoiceData,
          items: extractedItems,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      alert('Rechnung wurde der Baustelle zugeordnet und verschoben.');
      setSelectedItem(null);
      setInvoiceData(null);
      setDebugText('');
      setPreviewUrl('');
      setMarkedIndices([]);
      setSplitMode(false);
      setTargetFilename('');
      setTargetFilenameTouched(false);
      loadItems(currentPath);
    } catch (err) {
      console.error('Fehler beim Zuordnen der Rechnung:', err);
      alert(err.response?.data?.error || 'Fehler beim Zuordnen der Rechnung.');
    }
    setAssignLoading(false);
  }

  async function assignMarkedToBaustelle() {
    if (!selectedItem || selectedItem.is_dir) return;
    if (!selectedBaustelle) {
      alert('Bitte zuerst eine aktive Baustelle auswählen.');
      return;
    }
    if (!invoiceData || typeof invoiceData !== 'object') {
      alert('Bitte zuerst eine Analyse durchführen.');
      return;
    }
    if (!splitMode) {
      alert('Bitte zuerst den Modus "Rechnung splitten" aktivieren.');
      return;
    }
    if (!markedIndices.length) {
      alert('Bitte mindestens eine Position markieren.');
      return;
    }
    setAssignSelectedLoading(true);
    setInvoiceError('');
    try {
      await axios.post(
        `${API_BASE_URL}/baustellen/baustellen/rechnungen-split-zuordnen/`,
        {
          baustelle: selectedBaustelle,
          path: selectedItem.path,
          target_filename: (targetFilename || filenameSuggestion || '').trim(),
          invoice_data: invoiceData,
          items: extractedItems,
          selected_indices: markedIndices,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const remaining = extractedItems.filter((_, idx) => !markedIndices.includes(idx));
      setInvoiceData((prev) => ({ ...(prev || {}), items: remaining }));
      setMarkedIndices([]);
      setTargetFilenameTouched(false);
      alert('Markierte Positionen wurden der Baustelle zugeordnet. Die Rechnung wurde als Kopie abgelegt.');
    } catch (err) {
      console.error('Fehler beim Zuordnen markierter Positionen:', err);
      alert(err.response?.data?.error || 'Fehler beim Zuordnen markierter Positionen.');
    }
    setAssignSelectedLoading(false);
  }

  const isPdf = (selectedItem?.content_type || '').toLowerCase().includes('pdf') || (selectedItem?.name || '').toLowerCase().endsWith('.pdf');
  const isImage = (selectedItem?.content_type || '').toLowerCase().startsWith('image/');

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>Rechnungen</h2>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button className="btn" onClick={() => loadItems(currentPath)} disabled={loading}>
            <RefreshCw size={16} /> Aktualisieren
          </button>
          <button className="btn" onClick={() => loadItems('')} disabled={loading}>
            <FolderOpen size={16} /> Root
          </button>
          <button className="btn" onClick={goUp} disabled={loading || !currentPath}>
            <ArrowUp size={16} /> Eine Ebene hoch
          </button>
        </div>
      </div>

      <div style={{ marginBottom: '1rem', fontSize: '0.9rem', color: '#374151' }}>
        <strong>Aktueller Pfad:</strong> {currentPath ? `/${currentPath}` : '/'}
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Rechnungsqualität / Dashboard</h3>
            <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '0.2rem' }}>
              Operative Kennzahlen zu Review, Typisierung, Matching und Lieferantenqualität.
            </div>
          </div>
          <button className="btn" type="button" onClick={loadReports} disabled={reportLoading}>
            <RefreshCw size={16} /> {reportLoading ? 'Lade...' : 'Auswertung aktualisieren'}
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: '0.75rem', alignItems: 'end', marginBottom: '1rem' }}>
          <div>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.35rem', fontSize: '0.82rem' }}>Von</label>
            <input
              className="input"
              type="date"
              value={reportFilters.date_from}
              onChange={(e) => setReportFilters((prev) => ({ ...prev, date_from: e.target.value }))}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.35rem', fontSize: '0.82rem' }}>Bis</label>
            <input
              className="input"
              type="date"
              value={reportFilters.date_to}
              onChange={(e) => setReportFilters((prev) => ({ ...prev, date_to: e.target.value }))}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.35rem', fontSize: '0.82rem' }}>Baustelle</label>
            <select
              className="input"
              value={reportFilters.baustelle}
              onChange={(e) => setReportFilters((prev) => ({ ...prev, baustelle: e.target.value }))}
            >
              <option value="">Alle Baustellen</option>
              {sortedBaustellen.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.35rem', fontSize: '0.82rem' }}>Lieferant</label>
            <input
              className="input"
              type="text"
              value={reportFilters.issuer}
              onChange={(e) => setReportFilters((prev) => ({ ...prev, issuer: e.target.value }))}
              placeholder="z.B. Amazon"
            />
          </div>
          <button className="btn btn-primary" type="button" onClick={loadReports} disabled={reportLoading}>
            Anwenden
          </button>
        </div>

        {reportError ? (
          <div style={{ color: '#b91c1c' }}>{reportError}</div>
        ) : reportLoading && !reportOverview ? (
          <div style={{ color: '#6b7280' }}>Lade Auswertungen...</div>
        ) : reportOverview ? (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
              {[
                { label: 'Rechnungen gesamt', value: reportOverview.rechnungen?.total || 0, bg: '#eff6ff', color: '#1d4ed8' },
                { label: 'Offene Reviews', value: reportOverview.positions?.review_open || 0, bg: '#fef3c7', color: '#92400e' },
                { label: 'Kritische Positionen', value: reportOverview.positions?.review_critical || 0, bg: '#fee2e2', color: '#991b1b' },
                { label: 'Ohne Match', value: reportOverview.positions?.without_match || 0, bg: '#ede9fe', color: '#6d28d9' },
                { label: 'Manuell bestätigt', value: reportOverview.positions?.review_confirmed || 0, bg: '#d1fae5', color: '#065f46' },
              ].map((entry) => (
                <div key={entry.label} style={{ borderRadius: '0.5rem', padding: '0.75rem', background: entry.bg, color: entry.color }}>
                  <div style={{ fontSize: '0.78rem', fontWeight: 600 }}>{entry.label}</div>
                  <div style={{ fontSize: '1.2rem', fontWeight: 700 }}>{entry.value}</div>
                </div>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
              <div className="card" style={{ background: '#f9fafb' }}>
                <div style={{ fontWeight: 700, marginBottom: '0.5rem' }}>Positionsqualität</div>
                <div style={{ display: 'grid', gap: '0.25rem', fontSize: '0.85rem' }}>
                  <div>Mit Typ: <strong>{reportOverview.positions?.with_type || 0}</strong></div>
                  <div>Ohne Typ: <strong>{reportOverview.positions?.without_type || 0}</strong></div>
                  <div>Niedrige Typ-Confidence: <strong>{reportOverview.positions?.low_type_confidence || 0}</strong></div>
                  <div>Ø Typ-Confidence: <strong>{(reportOverview.positions?.avg_type_confidence || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></div>
                  <div>Mit Match: <strong>{reportOverview.positions?.with_match || 0}</strong></div>
                  <div>Schwache Matches: <strong>{reportOverview.positions?.weak_match || 0}</strong></div>
                  <div>Ø Match-Score: <strong>{(reportOverview.positions?.avg_match_score || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></div>
                </div>
              </div>
              <div className="card" style={{ background: '#f9fafb' }}>
                <div style={{ fontWeight: 700, marginBottom: '0.5rem' }}>Rechnungs- und Datenqualität</div>
                <div style={{ display: 'grid', gap: '0.25rem', fontSize: '0.85rem' }}>
                  <div>Rechnungen mit offenen Punkten: <strong>{reportOverview.rechnungen?.with_open_review || 0}</strong></div>
                  <div>Vollständig geprüft: <strong>{reportOverview.rechnungen?.fully_reviewed || 0}</strong></div>
                  <div>Ohne manuelle Korrektur: <strong>{reportOverview.rechnungen?.without_manual_corrections || 0}</strong></div>
                  <div>Ohne Beschreibung: <strong>{reportOverview.positions?.without_description || 0}</strong></div>
                  <div>Ohne Menge: <strong>{reportOverview.positions?.without_quantity || 0}</strong></div>
                  <div>Ohne Einheit: <strong>{reportOverview.positions?.without_unit || 0}</strong></div>
                  <div>Ohne Einzelpreis: <strong>{reportOverview.positions?.without_unit_price || 0}</strong></div>
                </div>
              </div>
            </div>

            <div>
              <div style={{ fontWeight: 700, marginBottom: '0.5rem' }}>Lieferantenqualität</div>
              <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', overflow: 'auto', maxHeight: '280px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                  <thead style={{ background: '#f9fafb', position: 'sticky', top: 0 }}>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '0.45rem', borderBottom: '1px solid #e5e7eb' }}>Lieferant</th>
                      <th style={{ textAlign: 'right', padding: '0.45rem', borderBottom: '1px solid #e5e7eb' }}>Rechnungen</th>
                      <th style={{ textAlign: 'right', padding: '0.45rem', borderBottom: '1px solid #e5e7eb' }}>Positionen</th>
                      <th style={{ textAlign: 'right', padding: '0.45rem', borderBottom: '1px solid #e5e7eb' }}>Unsicher</th>
                      <th style={{ textAlign: 'right', padding: '0.45rem', borderBottom: '1px solid #e5e7eb' }}>Offen</th>
                      <th style={{ textAlign: 'right', padding: '0.45rem', borderBottom: '1px solid #e5e7eb' }}>Manuell Typ</th>
                      <th style={{ textAlign: 'right', padding: '0.45rem', borderBottom: '1px solid #e5e7eb' }}>Manuell Match</th>
                      <th style={{ textAlign: 'right', padding: '0.45rem', borderBottom: '1px solid #e5e7eb' }}>Ø Typ</th>
                      <th style={{ textAlign: 'right', padding: '0.45rem', borderBottom: '1px solid #e5e7eb' }}>Ø Match</th>
                      <th style={{ textAlign: 'left', padding: '0.45rem', borderBottom: '1px solid #e5e7eb' }}>Alarm / Regel</th>
                    </tr>
                  </thead>
                  <tbody>
                    {supplierReport.length === 0 ? (
                      <tr>
                        <td colSpan={10} style={{ padding: '0.75rem', color: '#6b7280' }}>
                          Keine Lieferantendaten für die aktuelle Filterung vorhanden.
                        </td>
                      </tr>
                    ) : supplierReport.map((row) => (
                      <tr key={row.issuer}>
                        <td style={{ padding: '0.45rem', borderBottom: '1px solid #f3f4f6', fontWeight: 600 }}>{row.issuer}</td>
                        <td style={{ padding: '0.45rem', borderBottom: '1px solid #f3f4f6', textAlign: 'right' }}>{row.invoice_count}</td>
                        <td style={{ padding: '0.45rem', borderBottom: '1px solid #f3f4f6', textAlign: 'right' }}>{row.position_count}</td>
                        <td style={{ padding: '0.45rem', borderBottom: '1px solid #f3f4f6', textAlign: 'right' }}>{formatPercent(row.uncertain_rate)}</td>
                        <td style={{ padding: '0.45rem', borderBottom: '1px solid #f3f4f6', textAlign: 'right' }}>{formatPercent(row.review_open_rate)}</td>
                        <td style={{ padding: '0.45rem', borderBottom: '1px solid #f3f4f6', textAlign: 'right' }}>{formatPercent(row.manual_type_rate)}</td>
                        <td style={{ padding: '0.45rem', borderBottom: '1px solid #f3f4f6', textAlign: 'right' }}>{formatPercent(row.manual_match_rate)}</td>
                        <td style={{ padding: '0.45rem', borderBottom: '1px solid #f3f4f6', textAlign: 'right' }}>{(row.avg_type_confidence || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td style={{ padding: '0.45rem', borderBottom: '1px solid #f3f4f6', textAlign: 'right' }}>{(row.avg_match_score || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td style={{ padding: '0.45rem', borderBottom: '1px solid #f3f4f6', minWidth: '240px' }}>
                          <div style={{ display: 'grid', gap: '0.25rem' }}>
                            {row.rule_applied ? (
                              <div style={{ fontSize: '0.72rem', color: '#2563eb', fontWeight: 600 }}>
                                Regel: {row.rule_applied.issuer_name}
                              </div>
                            ) : (
                              <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>
                                Keine Lieferantenregel
                              </div>
                            )}
                            {Array.isArray(row.quality_alarms) && row.quality_alarms.length > 0 ? (
                              <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                                {row.quality_alarms.slice(0, 3).map((alarm) => (
                                  <span
                                    key={`${row.issuer}-${alarm.type}`}
                                    title={alarm.message || ''}
                                    style={{
                                      display: 'inline-flex',
                                      padding: '0.15rem 0.4rem',
                                      borderRadius: '999px',
                                      background: '#fee2e2',
                                      color: '#991b1b',
                                      fontSize: '0.7rem',
                                      fontWeight: 600,
                                    }}
                                  >
                                    {formatSupplierAlarmType(alarm.type)}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <div style={{ fontSize: '0.72rem', color: '#065f46' }}>
                                Keine Alarme
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <div style={{ color: '#6b7280' }}>Noch keine Auswertungsdaten vorhanden.</div>
        )}
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        {error ? (
          <div style={{ color: '#b91c1c' }}>{error}</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '25% 33% 1fr', gap: '1rem', minHeight: '520px' }}>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '0.5rem', display: 'grid', gridTemplateRows: '1fr auto', gap: '0.75rem', minHeight: '520px' }}>
              <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', overflow: 'auto' }}>
                {loading ? (
                  <div style={{ padding: '0.75rem', color: '#6b7280' }}>Lade Dateien...</div>
                ) : items.length === 0 ? (
                  <div style={{ padding: '0.75rem', color: '#6b7280' }}>Keine Dateien gefunden.</div>
                ) : (
                  items.map((item) => (
                    <button
                      key={`${item.is_dir ? 'd' : 'f'}-${item.path}`}
                      className="btn"
                      onClick={() => (item.is_dir ? loadItems(item.path) : openFile(item))}
                      onMouseEnter={() => setHoveredPath(item.path || '')}
                      onMouseLeave={() => setHoveredPath('')}
                      style={{
                        width: '100%',
                        justifyContent: 'flex-start',
                        borderRadius: 0,
                        borderBottom: '1px solid #f3f4f6',
                        background: selectedItem?.path === item.path && !item.is_dir
                          ? '#eff6ff'
                          : hoveredPath === item.path
                            ? '#2563eb'
                            : 'transparent',
                        color: hoveredPath === item.path ? '#ffffff' : undefined,
                      }}
                    >
                      {item.is_dir ? <FolderOpen size={14} /> : <FileText size={14} />}
                      <span style={{ textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.name}
                      </span>
                    </button>
                  ))
                )}
              </div>

              <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '0.75rem', fontSize: '0.85rem', overflow: 'auto', maxHeight: '240px' }}>
                <div style={{ fontWeight: 600, marginBottom: '0.4rem' }}>Analyse-Ergebnis</div>
                {invoiceLoading ? (
                  <div style={{ color: '#6b7280' }}>Lese Rechnung aus...</div>
                ) : invoiceError ? (
                  <div style={{ color: '#b91c1c' }}>{invoiceError}</div>
                ) : !invoiceData ? (
                  <div style={{ color: '#6b7280' }}>Noch keine Daten ausgelesen.</div>
                ) : (
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#111827' }}>
                    {JSON.stringify(invoiceData, null, 2)}
                  </pre>
                )}
              </div>

              <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '0.75rem', fontSize: '0.85rem', overflow: 'auto', maxHeight: '220px' }}>
                <div style={{ fontWeight: 600, marginBottom: '0.4rem' }}>Debug: erkannter PDF-Text</div>
                {debugLoading ? (
                  <div style={{ color: '#6b7280' }}>Lade Debug-Text...</div>
                ) : debugError ? (
                  <div style={{ color: '#b91c1c' }}>{debugError}</div>
                ) : !debugText ? (
                  <div style={{ color: '#6b7280' }}>Noch kein Debug-Text geladen.</div>
                ) : (
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#111827' }}>
                    {debugText}
                  </pre>
                )}
              </div>
            </div>

            <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '0.5rem', background: '#f9fafb' }}>
              {previewLoading ? (
                <div style={{ color: '#6b7280' }}>Lade Vorschau...</div>
              ) : previewError ? (
                <div style={{ color: '#b91c1c' }}>{previewError}</div>
              ) : !selectedItem ? (
                <div style={{ color: '#6b7280' }}>Bitte links eine Datei auswählen.</div>
              ) : !previewUrl ? (
                <div style={{ color: '#6b7280' }}>Keine Vorschau verfügbar.</div>
              ) : isPdf ? (
                <iframe
                  title="Rechnung Vorschau"
                  src={`${previewUrl}#navpanes=0&toolbar=0&scrollbar=1&view=FitH`}
                  style={{ width: '100%', height: '100%', minHeight: '480px', border: 'none' }}
                />
              ) : isImage ? (
                <img src={previewUrl} alt={selectedItem.name} style={{ width: '100%', height: 'auto', maxHeight: '500px', objectFit: 'contain' }} />
              ) : (
                <div style={{ color: '#6b7280' }}>
                  Vorschau für diesen Dateityp nicht verfügbar.
                </div>
              )}
            </div>

            <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '0.75rem' }}>
              <h3 style={{ marginTop: 0, marginBottom: '0.75rem', fontSize: '1rem' }}>Ausgelesene Daten</h3>
              {!selectedItem ? (
                <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>Noch keine Datei ausgewählt.</div>
              ) : (
                <div style={{ display: 'grid', gap: '0.5rem', fontSize: '0.9rem' }}>
                  <div><strong>Datei:</strong> {selectedItem.name}</div>
                  <div><strong>Typ:</strong> {selectedItem.content_type || '-'}</div>
                  <div><strong>Größe:</strong> {formatBytes(selectedItem.content_length)}</div>
                  <div><strong>Geändert:</strong> {selectedItem.last_modified || '-'}</div>
                  <div style={{ display: 'grid', gap: '0.35rem', marginTop: '0.25rem' }}>
                    <label style={{ fontWeight: 600 }}>Dateiname für Zuordnung:</label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.5rem' }}>
                      <input
                        className="input"
                        value={targetFilename}
                        onChange={(e) => {
                          setTargetFilename(e.target.value);
                          setTargetFilenameTouched(true);
                          setFilenameSuggestionApplied(false);
                        }}
                        placeholder="Dateiname.pdf"
                      />
                      <button
                        className="btn"
                        onClick={() => {
                          setTargetFilename(filenameSuggestion);
                          setTargetFilenameTouched(true);
                          setFilenameSuggestionApplied(true);
                        }}
                        style={filenameSuggestionApplied ? { backgroundColor: '#16a34a', borderColor: '#16a34a', color: '#ffffff' } : undefined}
                      >
                        Vorschlag übernehmen
                      </button>
                    </div>
                    <div style={{ color: '#6b7280', fontSize: '0.8rem' }}>
                      Vorschlag aus Muster: <strong>{filenameSuggestion}</strong>
                    </div>
                  </div>
                  {invoiceData && (
                    <>
                      <div style={{ display: 'grid', gap: '0.35rem' }}>
                        <label style={{ fontWeight: 600 }}>Lieferant:</label>
                        <input
                          className="input"
                          value={invoiceData.issuer ?? ''}
                          onChange={(e) => updateInvoiceHeaderField('issuer', e.target.value)}
                          placeholder="Lieferant"
                          list="supplier-suggestions"
                        />
                        <datalist id="supplier-suggestions">
                          {supplierSuggestions.map((supplier) => (
                            <option key={supplier} value={supplier} />
                          ))}
                        </datalist>
                      </div>
                      <div style={{ display: 'grid', gap: '0.35rem' }}>
                        <label style={{ fontWeight: 600 }}>Rechnungsnummer:</label>
                        <input
                          className="input"
                          value={invoiceData.invoice_number ?? ''}
                          onChange={(e) => updateInvoiceHeaderField('invoice_number', e.target.value)}
                          placeholder="Rechnungsnummer"
                        />
                      </div>
                      <div style={{ display: 'grid', gap: '0.35rem' }}>
                        <label style={{ fontWeight: 600 }}>Datum:</label>
                        <input
                          className="input"
                          value={invoiceData.date ?? ''}
                          onChange={(e) => updateInvoiceHeaderField('date', e.target.value)}
                          placeholder="TT.MM.JJJJ oder YYYY-MM-DD"
                        />
                      </div>
                      <div style={{ display: 'grid', gap: '0.35rem' }}>
                        <label style={{ fontWeight: 600 }}>Betrag:</label>
                        <input
                          className="input"
                          value={invoiceData.amount ?? ''}
                          onChange={(e) => updateInvoiceHeaderField('amount', e.target.value)}
                          placeholder="z.B. 13,79"
                        />
                      </div>
                      <div style={{ display: 'grid', gap: '0.35rem' }}>
                        <label style={{ fontWeight: 600 }}>Währung:</label>
                        <input
                          className="input"
                          value={invoiceData.currency ?? ''}
                          onChange={(e) => updateInvoiceHeaderField('currency', e.target.value)}
                          placeholder="EUR"
                        />
                      </div>
                      <div><strong>Posten erkannt:</strong> {extractedItems.length}</div>
                      {invoiceData.items_source && (
                        <div><strong>Posten-Quelle:</strong> {invoiceData.items_source}</div>
                      )}
                    </>
                  )}
                  {extractedItems.length > 0 && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <div
                        className="card"
                        style={{
                          marginBottom: '0.75rem',
                          background: '#f9fafb',
                          border: '1px solid #e5e7eb',
                          padding: '0.75rem',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                          <div>
                            <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>Review / Freigabe</div>
                            <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                              {reviewSummary.open} von {reviewSummary.total} Positionen brauchen Aufmerksamkeit.
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <button className="btn" type="button" onClick={jumpToNextReviewItem} disabled={!reviewSummary.open}>
                              Nächste offene
                            </button>
                          </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: '0.5rem', marginTop: '0.75rem' }}>
                          {[
                            { label: 'Positionen', value: reviewSummary.total, bg: '#eff6ff', color: '#1d4ed8' },
                            { label: 'Prüfbedürftig', value: reviewSummary.open, bg: '#fef3c7', color: '#92400e' },
                            { label: 'Niedrige Confidence', value: reviewSummary.lowConfidence, bg: '#fee2e2', color: '#991b1b' },
                            { label: 'Ohne Match', value: reviewSummary.withoutMatch, bg: '#ede9fe', color: '#6d28d9' },
                            { label: 'Geprüft/manuell', value: reviewSummary.confirmed, bg: '#d1fae5', color: '#065f46' },
                          ].map((entry) => (
                            <div key={entry.label} style={{ borderRadius: '0.5rem', padding: '0.6rem 0.75rem', background: entry.bg, color: entry.color }}>
                              <div style={{ fontSize: '0.75rem', fontWeight: 600 }}>{entry.label}</div>
                              <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>{entry.value}</div>
                            </div>
                          ))}
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
                          {[
                            { id: 'all', label: 'Alle' },
                            { id: 'review', label: 'Nur prüfbedürftige' },
                            { id: 'withoutType', label: 'Ohne Typ' },
                            { id: 'withoutMatch', label: 'Ohne Match' },
                            { id: 'lowConfidence', label: 'Niedrige Confidence' },
                            { id: 'manual', label: 'Manuell/geprüft' },
                          ].map((entry) => (
                            <button
                              key={entry.id}
                              className={`btn ${reviewFilter === entry.id ? 'btn-primary' : ''}`}
                              type="button"
                              onClick={() => setReviewFilter(entry.id)}
                            >
                              {entry.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div style={{ fontWeight: 600, marginBottom: '0.35rem' }}>
                        Positionen {splitMode ? '(Split-Modus aktiv: Zeilen anklicken)' : ''}
                        {reviewFilter !== 'all' ? ` • Filter: ${filteredReviewedItems.length}` : ''}
                      </div>
                      <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.4rem', overflow: 'auto', maxHeight: '320px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                          <thead style={{ background: '#f9fafb', position: 'sticky', top: 0 }}>
                            <tr>
                              {splitMode && <th style={{ textAlign: 'center', padding: '0.4rem', borderBottom: '1px solid #e5e7eb' }}>Mark.</th>}
                              <th style={{ textAlign: 'left', padding: '0.4rem', borderBottom: '1px solid #e5e7eb', minWidth: '160px' }}>Review</th>
                              <th style={{ textAlign: 'left', padding: '0.4rem', borderBottom: '1px solid #e5e7eb' }}>Art.-Nr.</th>
                              <th style={{ textAlign: 'left', padding: '0.4rem', borderBottom: '1px solid #e5e7eb' }}>Beschreibung</th>
                              <th style={{ textAlign: 'right', padding: '0.4rem', borderBottom: '1px solid #e5e7eb' }}>Menge</th>
                              <th style={{ textAlign: 'left', padding: '0.4rem', borderBottom: '1px solid #e5e7eb' }}>Einheit</th>
                              <th style={{ textAlign: 'right', padding: '0.4rem', borderBottom: '1px solid #e5e7eb' }}>Einzelpreis</th>
                              <th style={{ textAlign: 'right', padding: '0.4rem', borderBottom: '1px solid #e5e7eb' }}>Betrag</th>
                              <th style={{ textAlign: 'left', padding: '0.4rem', borderBottom: '1px solid #e5e7eb', minWidth: '240px' }}>Typ</th>
                              <th style={{ textAlign: 'left', padding: '0.4rem', borderBottom: '1px solid #e5e7eb', minWidth: '280px' }}>Match</th>
                              <th style={{ textAlign: 'right', padding: '0.4rem', borderBottom: '1px solid #e5e7eb' }}>Aktion</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredReviewedItems.map(({ row, index: idx, review }) => (
                              (() => {
                                const confidenceMeta = getConfidenceMeta(row?.type_confidence);
                                const candidateSummary = Array.isArray(row?.type_candidates)
                                  ? row.type_candidates
                                    .filter((candidate) => candidate?.key && candidate.key !== row?.type_suggestion?.key)
                                    .slice(0, 3)
                                  : [];
                                const selectedTypeId = getSuggestedTypeId(row, activeRechnungspositionsTypen);
                                const primaryMatch = getPrimaryMatch(row);
                                const matchCandidates = getMatchCandidates(row);
                                const matchMeta = getMatchMeta(row?.match_score ?? primaryMatch?.score ?? primaryMatch?.match_score);
                                const reviewMeta = getReviewMeta(review.status);
                                return (
                              <tr
                                id={`invoice-row-${idx}`}
                                key={`${row.article_no || 'x'}-${idx}`}
                                onClick={() => toggleItemMark(idx)}
                                style={{
                                  cursor: splitMode ? 'pointer' : 'default',
                                  background: markedIndices.includes(idx) ? '#fef3c7' : reviewMeta.background,
                                }}
                              >
                                {splitMode && (
                                  <td style={{ padding: '0.35rem', borderBottom: '1px solid #f3f4f6', textAlign: 'center' }}>
                                    {markedIndices.includes(idx) ? 'x' : ''}
                                  </td>
                                )}
                                <td style={{ padding: '0.35rem', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' }}>
                                  <div style={{ display: 'grid', gap: '0.35rem' }} onClick={(e) => e.stopPropagation()}>
                                    <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                      <span
                                        style={{
                                          display: 'inline-flex',
                                          padding: '0.15rem 0.4rem',
                                          borderRadius: '999px',
                                          background: reviewMeta.background,
                                          color: reviewMeta.color,
                                          border: `1px solid ${reviewMeta.border}`,
                                          fontWeight: 700,
                                        }}
                                      >
                                        {reviewMeta.label}
                                      </span>
                                      {row?.review_confirmed && (
                                        <span style={{ color: '#065f46', fontWeight: 600 }}>geprüft</span>
                                      )}
                                    </div>
                                    {review.reasons.slice(0, 3).map((reason) => (
                                      <div
                                        key={reason}
                                        style={{
                                          fontSize: '0.72rem',
                                          color: review.status === 'kritisch' ? '#991b1b' : '#6b7280',
                                        }}
                                      >
                                        {reason}
                                      </div>
                                    ))}
                                    <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                                      <button
                                        className="btn"
                                        type="button"
                                        style={{ padding: '0.2rem 0.45rem', fontSize: '0.72rem' }}
                                        onClick={() => confirmReview(idx)}
                                      >
                                        Review ok
                                      </button>
                                      {row?.review_confirmed && (
                                        <button
                                          className="btn"
                                          type="button"
                                          style={{ padding: '0.2rem 0.45rem', fontSize: '0.72rem' }}
                                          onClick={() => resetReview(idx)}
                                        >
                                          Zurücksetzen
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td style={{ padding: '0.35rem', borderBottom: '1px solid #f3f4f6' }}>
                                  <input
                                    className="input"
                                    style={{ fontSize: '0.8rem', padding: '0.2rem 0.35rem' }}
                                    value={row.article_no ?? ''}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => updateExtractedItem(idx, 'article_no', e.target.value)}
                                  />
                                </td>
                                <td style={{ padding: '0.35rem', borderBottom: '1px solid #f3f4f6' }}>
                                  <input
                                    className="input"
                                    style={{ fontSize: '0.8rem', padding: '0.2rem 0.35rem' }}
                                    value={row.description ?? ''}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => updateExtractedItem(idx, 'description', e.target.value)}
                                  />
                                </td>
                                <td style={{ padding: '0.35rem', borderBottom: '1px solid #f3f4f6', textAlign: 'right' }}>
                                  <input
                                    className="input"
                                    style={{ fontSize: '0.8rem', padding: '0.2rem 0.35rem', textAlign: 'right' }}
                                    value={row.quantity ?? ''}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => updateExtractedItem(idx, 'quantity', e.target.value)}
                                  />
                                </td>
                                <td style={{ padding: '0.35rem', borderBottom: '1px solid #f3f4f6' }}>
                                  <input
                                    className="input"
                                    style={{ fontSize: '0.8rem', padding: '0.2rem 0.35rem' }}
                                    value={row.unit ?? ''}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => updateExtractedItem(idx, 'unit', e.target.value)}
                                  />
                                </td>
                                <td style={{ padding: '0.35rem', borderBottom: '1px solid #f3f4f6', textAlign: 'right' }}>
                                  <input
                                    className="input"
                                    style={{ fontSize: '0.8rem', padding: '0.2rem 0.35rem', textAlign: 'right' }}
                                    value={row.unit_price ?? ''}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => updateExtractedItem(idx, 'unit_price', e.target.value)}
                                  />
                                </td>
                                <td style={{ padding: '0.35rem', borderBottom: '1px solid #f3f4f6', textAlign: 'right' }}>
                                  <input
                                    className="input"
                                    style={{ fontSize: '0.8rem', padding: '0.2rem 0.35rem', textAlign: 'right' }}
                                    value={row.line_total ?? ''}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => updateExtractedItem(idx, 'line_total', e.target.value)}
                                  />
                                </td>
                                <td style={{ padding: '0.35rem', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' }}>
                                  <div style={{ display: 'grid', gap: '0.35rem' }} onClick={(e) => e.stopPropagation()}>
                                    <select
                                      className="input"
                                      value={selectedTypeId}
                                      onChange={(e) => handleTypeSelection(idx, e.target.value)}
                                      disabled={!activeRechnungspositionsTypen.length}
                                      style={{ fontSize: '0.8rem', padding: '0.2rem 0.35rem' }}
                                    >
                                      <option value="">Typ wählen...</option>
                                      {activeRechnungspositionsTypen.map((type) => (
                                        <option key={type.id} value={type.id}>
                                          {type.label}
                                        </option>
                                      ))}
                                    </select>
                                    <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center', flexWrap: 'wrap', fontSize: '0.72rem' }}>
                                      <span
                                        style={{
                                          display: 'inline-flex',
                                          padding: '0.15rem 0.4rem',
                                          borderRadius: '999px',
                                          background: confidenceMeta.background,
                                          color: confidenceMeta.color,
                                          fontWeight: 600,
                                        }}
                                      >
                                        {confidenceMeta.badge}
                                      </span>
                                      <span style={{ color: '#374151' }}>{confidenceMeta.label}</span>
                                      <span style={{ color: '#6b7280' }}>{row?.type_source || '-'}</span>
                                      {(row?.typ_manually_confirmed || row?.type_source === 'manual' || row?.typ_source === 'manual') && (
                                        <span style={{ color: '#2563eb', fontWeight: 600 }}>manuell</span>
                                      )}
                                    </div>
                                    {Array.isArray(row?.type_reasoning) && row.type_reasoning.length > 0 && (
                                      <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>
                                        {row.type_reasoning[0]}
                                      </div>
                                    )}
                                    {candidateSummary.length > 0 && (
                                      <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>
                                        Alternativen:{' '}
                                        {candidateSummary.map((candidate) => {
                                          const candidateConfidence = getConfidenceMeta(candidate?.confidence);
                                          return `${candidate.label || candidate.key} (${candidateConfidence.label})`;
                                        }).join(' • ')}
                                      </div>
                                    )}
                                    {rechnungspositionsTypenError && (
                                      <div style={{ fontSize: '0.72rem', color: '#b91c1c' }}>
                                        {rechnungspositionsTypenError}
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td style={{ padding: '0.35rem', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' }}>
                                  <div style={{ display: 'grid', gap: '0.35rem' }} onClick={(e) => e.stopPropagation()}>
                                    <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center', flexWrap: 'wrap', fontSize: '0.72rem' }}>
                                      <span
                                        style={{
                                          display: 'inline-flex',
                                          padding: '0.15rem 0.4rem',
                                          borderRadius: '999px',
                                          background: matchMeta.background,
                                          color: matchMeta.color,
                                          fontWeight: 600,
                                        }}
                                      >
                                        {matchMeta.badge}
                                      </span>
                                      <span style={{ color: '#374151' }}>{matchMeta.label}</span>
                                      <span style={{ color: '#6b7280' }}>{formatMatchSource(row?.match_source || primaryMatch?.source)}</span>
                                      {(row?.match_status === 'confirmed' || row?.match_manually_confirmed) && (
                                        <span style={{ color: '#2563eb', fontWeight: 600 }}>übernommen</span>
                                      )}
                                      {row?.match_status === 'rejected' && (
                                        <span style={{ color: '#991b1b', fontWeight: 600 }}>ignoriert</span>
                                      )}
                                    </div>

                                    <div style={{ fontSize: '0.78rem', color: '#111827', fontWeight: 600 }}>
                                      {primaryMatch ? getMatchCandidateLabel(primaryMatch) : 'Kein aktiver Vorschlag'}
                                    </div>

                                    {Array.isArray(primaryMatch?.reasoning) && primaryMatch.reasoning.length > 0 && (
                                      <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>
                                        {primaryMatch.reasoning[0]}
                                      </div>
                                    )}

                                    {matchCandidates.length > 0 && (
                                      <div style={{ display: 'grid', gap: '0.3rem' }}>
                                        {matchCandidates.slice(0, 3).map((candidate, candidateIdx) => {
                                          const candidateMeta = getMatchMeta(candidate?.score ?? candidate?.match_score);
                                          return (
                                            <div
                                              key={`${candidate.source || 'match'}-${candidate.artikel_id || candidate.kostenposition_id || candidateIdx}`}
                                              style={{
                                                border: '1px solid #e5e7eb',
                                                borderRadius: '0.4rem',
                                                padding: '0.35rem 0.45rem',
                                                background: primaryMatch === candidate ? '#f8fafc' : '#ffffff',
                                              }}
                                            >
                                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', alignItems: 'center' }}>
                                                <div style={{ minWidth: 0 }}>
                                                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#111827' }}>
                                                    {getMatchCandidateLabel(candidate)}
                                                  </div>
                                                  <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                                                    {formatMatchSource(candidate?.source)} • {candidateMeta.label}
                                                  </div>
                                                </div>
                                                <button
                                                  className="btn"
                                                  type="button"
                                                  style={{ padding: '0.2rem 0.45rem', fontSize: '0.72rem' }}
                                                  onClick={() => applyMatchCandidate(idx, candidate)}
                                                >
                                                  Übernehmen
                                                </button>
                                              </div>
                                              {Array.isArray(candidate?.reasoning) && candidate.reasoning.length > 0 && (
                                                <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '0.2rem' }}>
                                                  {candidate.reasoning[0]}
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}

                                    <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                                      {primaryMatch && (
                                        <button
                                          className="btn"
                                          type="button"
                                          style={{ padding: '0.2rem 0.45rem', fontSize: '0.72rem' }}
                                          onClick={() => applyMatchCandidate(idx, primaryMatch)}
                                        >
                                          Vorschlag übernehmen
                                        </button>
                                      )}
                                      <button
                                        className="btn"
                                        type="button"
                                        style={{ padding: '0.2rem 0.45rem', fontSize: '0.72rem', color: '#991b1b' }}
                                        onClick={() => ignoreMatch(idx)}
                                      >
                                        Ignorieren
                                      </button>
                                    </div>
                                  </div>
                                </td>
                                <td style={{ padding: '0.35rem', borderBottom: '1px solid #f3f4f6', textAlign: 'right' }}>
                                  <button
                                    className="btn"
                                    style={{ color: '#dc2626', padding: '0.25rem 0.5rem' }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      removeExtractedItem(idx);
                                    }}
                                  >
                                    Löschen
                                  </button>
                                </td>
                              </tr>
                                );
                              })()
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div style={{ marginTop: '0.4rem', textAlign: 'right', fontWeight: 700, fontSize: '0.85rem' }}>
                        Summe Posten: {extractedItemsTotal.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ fontSize: '0.875rem', color: '#6b7280', marginRight: '0.25rem' }}>
            Dateien: {fileItems.length}
          </div>
          <select
            className="input"
            value={selectedAnalyzer}
            onChange={(e) => setSelectedAnalyzer(e.target.value)}
            style={{ minWidth: '190px' }}
          >
            <option value="invoice2data">Analyse: invoice2data</option>
            <option value="llm">Analyse: LLM</option>
          </select>
          <button className="btn btn-primary" onClick={extractWithInvoice2Data} disabled={!selectedItem || invoiceLoading || invoiceOcrLoading}>
            Analyse starten
          </button>
          <button className="btn btn-primary" onClick={extractWithOcr} disabled={!selectedItem || invoiceLoading || invoiceOcrLoading}>
            {invoiceOcrLoading ? 'OCR läuft...' : 'OCR + Analyse'}
          </button>
          <button className="btn" onClick={loadDebugText} disabled={!selectedItem || debugLoading}>
            {debugLoading ? 'Debug lädt...' : 'Debug-Text anzeigen'}
          </button>
          <button className="btn" onClick={loadDebugTextWithOcr} disabled={!selectedItem || debugLoading}>
            {debugLoading ? 'Debug lädt...' : 'Debug-Text (OCR)'}
          </button>
          <button className="btn" onClick={generateTemplateWithLlm} disabled={!selectedItem || templateLoading}>
            {templateLoading ? 'Template wird erstellt...' : 'Template per LLM erstellen'}
          </button>
          <button className="btn" onClick={downloadSelected} disabled={!selectedItem || !previewUrl}>
            <Download size={16} /> Datei herunterladen
          </button>
          <button className="btn" onClick={() => loadItems(currentPath)} disabled={loading}>
            <RefreshCw size={16} /> Neu laden
          </button>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
          <select
            className="input"
            value={selectedBaustelle}
            onChange={(e) => setSelectedBaustelle(e.target.value)}
            style={{
              minWidth: '240px',
              background: selectedBaustelleObj?.farbe || '#ffffff',
              color: selectedBaustelleObj?.farbe ? getContrastTextColor(selectedBaustelleObj.farbe) : '#111827',
            }}
          >
            <option value="">Aktive Baustelle wählen...</option>
            {sortedBaustellen.map((b) => (
              <option
                key={b.id}
                value={b.id}
                style={{
                  background: b.farbe || '#ffffff',
                  color: getContrastTextColor(b.farbe || '#ffffff'),
                }}
              >
                {b.nummer} - {b.name}
              </option>
            ))}
          </select>
          <button
            className="btn btn-primary"
            onClick={assignInvoiceToBaustelle}
            disabled={!selectedItem || !invoiceData || assignLoading || assignSelectedLoading}
          >
            {assignLoading ? 'Zuordnung läuft...' : 'Zuordnen'}
          </button>
          <button
            className={`btn ${splitMode ? 'btn-primary' : ''}`}
            onClick={() => {
              setSplitMode((prev) => !prev);
              setMarkedIndices([]);
            }}
            disabled={!invoiceData || !extractedItems.length}
          >
            Rechnung splitten
          </button>
          <button
            className="btn btn-primary"
            onClick={assignMarkedToBaustelle}
            disabled={!splitMode || !markedIndices.length || assignSelectedLoading || assignLoading}
          >
            {assignSelectedLoading ? 'Zuordnung läuft...' : 'Markierte Positionen Baustelle zuordnen'}
          </button>
          <button
            className="btn"
            onClick={openAddItemModal}
            disabled={!invoiceData}
          >
            Posten manuell hinzufügen
          </button>
        </div>
      </div>

      {showAddItemModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            zIndex: 2460,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem'
          }}
        >
          <div className="card" style={{ width: '100%', maxWidth: '640px' }}>
            <h3 style={{ marginTop: 0, marginBottom: '0.75rem', fontSize: '1.1rem' }}>Posten manuell anlegen</h3>
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.4rem', fontSize: '0.875rem' }}>Art.-Nr.</label>
                  <input className="input" value={newItemForm.article_no} onChange={(e) => setNewItemForm((prev) => ({ ...prev, article_no: e.target.value }))} />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.4rem', fontSize: '0.875rem' }}>Einheit</label>
                  <input className="input" value={newItemForm.unit} onChange={(e) => setNewItemForm((prev) => ({ ...prev, unit: e.target.value }))} />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.4rem', fontSize: '0.875rem' }}>Beschreibung</label>
                <textarea className="input" rows={3} value={newItemForm.description} onChange={(e) => setNewItemForm((prev) => ({ ...prev, description: e.target.value }))} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.4rem', fontSize: '0.875rem' }}>Menge</label>
                  <input className="input" value={newItemForm.quantity} onChange={(e) => setNewItemForm((prev) => ({ ...prev, quantity: e.target.value }))} />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.4rem', fontSize: '0.875rem' }}>Einzelpreis</label>
                  <input className="input" value={newItemForm.unit_price} onChange={(e) => setNewItemForm((prev) => ({ ...prev, unit_price: e.target.value }))} />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.4rem', fontSize: '0.875rem' }}>Betrag</label>
                  <input className="input" value={newItemForm.line_total} onChange={(e) => setNewItemForm((prev) => ({ ...prev, line_total: e.target.value }))} />
                </div>
              </div>
            </div>
            <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button className="btn" onClick={closeAddItemModal}>Abbrechen</button>
              <button className="btn btn-primary" onClick={saveManualItem}>Posten hinzufügen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RechnungenView;
